import { supabase } from '../lib/supabase';
import { offeredElectronicNotice } from './legalDocumentsService';

/**
 * Client for the public redeem-invite Edge Function (app-owned invite tokens).
 * The raw token is the credential; it is sent only in the POST body and is
 * never persisted to storage.
 */
const FUNCTION_NAME = 'redeem-invite';
const REQUEST_TIMEOUT_MS = 45000;

const getFunctionUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Supabase URL is not configured.');
  return `${supabaseUrl.replace(/\/$/, '')}/functions/v1/${FUNCTION_NAME}`;
};

const getAccessToken = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch (e) {
    return null;
  }
};

const postToFunction = async (payload, { includeSession = false } = {}) => {
  const headers = {
    apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
    'Content-Type': 'application/json',
  };
  if (includeSession) {
    const accessToken = await getAccessToken();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(getFunctionUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    let data = {};
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }
    return { status: response.status, data };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return {
        status: 0,
        data: { ok: false, reason: 'network', error: 'The request timed out. Please try again.' },
      };
    }
    return {
      status: 0,
      data: {
        ok: false,
        reason: 'network',
        error: 'We could not reach the portal. Check your connection and try again.',
      },
    };
  } finally {
    clearTimeout(timeoutId);
  }
};

/** Read-only look-up of an invitation link. Sends the current session (if any)
 * so the server can report whether it belongs to the invited account. */
export const previewInvite = async (token) => {
  const { data } = await postToFunction({ action: 'preview', token }, { includeSession: true });
  return data || { ok: false, reason: 'network' };
};

/**
 * Builds the redeem request's `legal_acceptance` from the preview's `legal`
 * block and the checkbox values. Echoes the server's document ids/versions
 * exactly; returns undefined when there is no legal block (nothing to accept).
 * The electronic-notice choice is sent whenever that checkbox is shown (same
 * rule as LegalConsentFields: offeredElectronicNotice), so an explicit "no" is
 * recorded as well as a "yes", and nothing is sent for a box that was not shown.
 */
export const buildLegalAcceptance = (legal, { agreedRequired, electronicNoticeGranted } = {}) => {
  if (!legal || typeof legal !== 'object') return undefined;
  const required = Array.isArray(legal.required) ? legal.required : [];
  const notice = offeredElectronicNotice(legal);
  const acceptance = {
    agreed_required: agreedRequired === true,
    documents: required.map((doc) => ({
      doc_type: doc.doc_type,
      document_id: doc.document_id,
      version: doc.version,
    })),
  };
  if (notice) {
    acceptance.electronic_notice = {
      document_id: notice.document_id,
      version: notice.version,
      granted: electronicNoticeGranted === true,
    };
  }
  return acceptance;
};

/** Creates the account for a valid invitation. Returns { status, data }.
 * `legalAcceptance` is added to the body only when provided. */
export const redeemInvite = async ({ token, password, firstName, lastName, phone, legalAcceptance }) =>
  postToFunction({
    action: 'redeem',
    token,
    password,
    first_name: firstName,
    last_name: lastName,
    phone,
    ...(legalAcceptance ? { legal_acceptance: legalAcceptance } : {}),
  });
